import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Badge,
  Divider,
  Box,
  CircularProgress,
  Tooltip
} from '@material-ui/core';
import {
  setPostAcqHasInputLimitError,
  setPostAcqSelectedValuation,
  setPostAcqTransientValuationInputs,
  setPostAcqTransientValuationNotes,
  savePostAcqModuleUW,
  appNotify,
  setPostAcqTransientValuation,
  closePostAcqTasks,
  openPostAcqTasks,
} from 'store/actions';
import {
  isUsersPostAcqValuationSelector,
  selectedPostAcqProformaInputsSelector,
  nextTaskSelector,
  postAcquisitionTaxonomySelector
} from 'store/selectors';
import {
  PostAcqUWProformaInput
} from 'components/underwrite/postAcquisition/postAcqUnderwrite';
import { getRequiredProformaInputs, getRequiresUnderwriteManager } from 'components/underwrite/postAcquisition/import/importTasks/taxonomyHelpers';
import { sendClientLog } from 'services/logService';
import { NoteModal } from 'components/underwrite/shared';
import classNames from 'classnames';
import { Constants } from 'utilities/constants';
import { parseISO, format } from 'date-fns';

const PostAcqUWProforma = ({
  classes,
  history,
  user,
  userHasUnderwriteReadOnlyRole,
  selectedOpportunity,
  selectedContainer,
  selectedProformaInputs,
  valuations,
  selectedValuation,
  transientValuation,
  valuationIsDirty,
  hasInputLimitError,
  isUsersValuation,
  isSavingUW,
  nextTask,
  taxonomy,
  handleModalClose,
  viewUnderwriteModalIsOpen,
  viewingReadonlyMode,
  closePostAcqTasks,
  openPostAcqTasks,
  setPostAcqHasInputLimitError,
  setPostAcqSelectedValuation,
  setPostAcqTransientValuation,
  setPostAcqTransientValuationInputs,
  setPostAcqTransientValuationNotes,
  savePostAcqModuleUW,
  appNotify
}) => {
  const [proformaValuation, setProformaValuation] = useState(selectedValuation) // This state variable stores the transient valution/selected valuation (whatever is shown/editable on screen)
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);

  useEffect(() => { /**Trigger when transient valuation updates */
    if (isUsersValuation) {
      setProformaValuation(transientValuation);
    }
  }, [transientValuation]);

  useEffect(() => { /**Triggers when using dropdown to select user valuation. Show transient values for user's own valuation */
    const valuation = isUsersValuation ? transientValuation : selectedValuation;
    setProformaValuation(valuation);
  }, [selectedValuation]);

  const requiredFields = useMemo(
    () => getRequiredProformaInputs(
      taxonomy?.entries ?? [],
      selectedOpportunity?.channel,
      selectedOpportunity?.requestType
    ),
    [taxonomy?.entries, selectedOpportunity?.channel, selectedOpportunity?.requestType]
  );

  // Some (Channel, RequestType) pairs (e.g. For Sale Analysis) require the Underwrite
  // Manager role to save. Defaults to false for unknown pairs.
  const userLacksRequiredRole = useMemo(() => {
    return false; // Not disabling in UI for now, use backend to return error
    const requiresManager = getRequiresUnderwriteManager(
      taxonomy?.entries ?? [],
      selectedOpportunity?.channel,
      selectedOpportunity?.requestType
    );
    return requiresManager && !user?.roles?.underwriteManager;
  }, [taxonomy?.entries, selectedOpportunity?.channel, selectedOpportunity?.requestType, user?.roles?.underwriteManager]);

  if (!selectedOpportunity) return;
  /**Triggers when user chooses valuation from dropdown */
  const onValuationIndexChange = e => {
    const newSelectedValuation = valuations[e.target.value];
    setPostAcqSelectedValuation(newSelectedValuation);
  }

  /**Triggers when user click 'Take Valuation' */
  const takeUWClick = () => {
    // create new transient valuation object but with other person's valuation
    const newTransientValuation = {
      ...selectedValuation,
      createdOn: transientValuation.createdOn,
      id: transientValuation.id,
      user: transientValuation.user,
      takenValuationId: selectedValuation.id
    }

    setPostAcqSelectedValuation(valuations.find(x => x.user.id === user.id), false);      // set back to user's valuation
    setPostAcqTransientValuation(newTransientValuation);
    appNotify(`Took ${selectedValuation.user.fullName}'s Underwrite`, 'success', { autoHideDuration: 15000000 });
  }

  /**This gets passed into 'PropertyUWProformaInput' component as change handler */
  const handlePropertyUWProformaInputChange = (newInput) => {
    if (isUsersValuation) {
      let newValuationInput = { ...proformaValuation.valuationInput };
      newValuationInput[newInput.inputGroupName] = newInput.selectedRadioValue;
      newValuationInput[newInput.inputGroupName + 'Type'] = newInput.selectedRadioType;

      setPostAcqTransientValuationInputs(newValuationInput, true); // Pass true to skip setting shouldCalculateProforma flag
    }
  }

  /**This gets passed into 'PropertyUWProformaInput' component to handle error*/
  const handleInputError = (errorMsg) => {
    appNotify(errorMsg, 'warning');
  }

  const setInputLimitError = (inputGroup, hasError) => { 
    if (viewingReadonlyMode) return;
    setPostAcqHasInputLimitError(inputGroup, hasError); 
  } 

  /**Open notes modal */
  const onClickNoteSection = () => {
    setIsNoteModalOpen(true);
  }

  const onCancelNotes = () => {
    setIsNoteModalOpen(false);
  }

  const onUpdateNotes = (notesValue) => {
    const notes = notesValue !== '' ? notesValue : null
    if (isUsersValuation) {
      setPostAcqTransientValuationNotes(notes);
    }
  }

  const PROFORMA_FIELDS = ['rent', 'afterRepairValue', 'lotValue', 'asIsValue', 'reducedRent', 'reducedAfterRepairValue'];
  const isInputPresent = v => v != null && v !== '';

  const hasRequiredValues = () => {
    const { valuationInput } = transientValuation;
    if (requiredFields && requiredFields.length > 0) {
      return requiredFields.every(field => isInputPresent(valuationInput[field]));
    }
    return PROFORMA_FIELDS.some(field => isInputPresent(valuationInput[field]));
  }

  const missingRequiredValuesMessage = () =>
    (requiredFields && requiredFields.length > 0)
      ? 'Cannot save with missing required values'
      : 'Cannot save without at least one value';

  const saveUWClick = async () => {
    if (!hasRequiredValues()) {
      appNotify(missingRequiredValuesMessage(), 'error');
      return;
    }

    sendClientLog('Info', `${viewUnderwriteModalIsOpen ? 'Explore ' : 'Post-Acq UW'} - User clicked Save on proforma. postAcqTaskId ${selectedOpportunity.id}`);

    const saveUWPayload = {
      taskId: selectedOpportunity.id,
      transientValuation
    }
    savePostAcqModuleUW(saveUWPayload);
  }

  const saveAndNavigateToNextTask = async () => {
    const buttonType = valuationIsDirty ? 'Save and Next' : 'Next';
    sendClientLog('Info', `Post-Acq UW - User clicked ${buttonType} on proforma. postAcqTaskId ${selectedOpportunity.id}`);

    const nextTaskId = nextTask ? nextTask.id : null;
    if (!canSave) {
      // just navigate to next if nothing needs to be saved.
      navigateToNextOpportunity(nextTaskId);
      return;
    }

    if (!hasRequiredValues()) {
      appNotify(missingRequiredValuesMessage(), 'error');
      return;
    }

    const saveUWPayload = {
      taskId: selectedOpportunity.id,
      transientValuation,
      history,
      nextTaskId
    }

    savePostAcqModuleUW(saveUWPayload)
  }
  
  const navigateToNextOpportunity = (navigateToId) => {
    if (navigateToId) {
      history.push(`${Constants.Routes.POSTACQ_TASK}/${navigateToId}`);
    }
  }

  const saveAndCloseModal = () => {
    const buttonType = canSave ? 'Save and Close' : 'Close';
    sendClientLog('Info', `Explore - User clicked ${buttonType} on proforma. postAcqTaskId ${selectedOpportunity.id}`);

    if (!canSave) {
      // just navigate to next if nothing needs to be saved.
      handleModalClose();
      return;
    }

    if (!hasRequiredValues()) {
      appNotify(missingRequiredValuesMessage(), 'error');
      return;
    }

    const saveUWPayload = {
      taskId: selectedOpportunity.id,
      transientValuation
    }
    savePostAcqModuleUW(saveUWPayload)
    handleModalClose();
  }

  const handleTaskStatusChange = () => {
    const action = selectedOpportunity.taskStatus === Constants.PostAcqTaskStatus.OPEN ? closePostAcqTasks : openPostAcqTasks;
    action([selectedOpportunity.id])
  }

  const canSave = valuationIsDirty || transientValuation?.id === 0; // Valuation can be saved if persisted valuation is modified, or if no persisted valuation exists (transient id of 0)
  const canEdit = isUsersValuation && !viewingReadonlyMode;
  const foundSelectedValuationIndex = valuations.findIndex(
    val => selectedValuation ? val.id === selectedValuation.id : false
  );
  const isWithinValidBedBathRange = selectedOpportunity.beds <= 10 && selectedOpportunity.baths <= 10; // AT-2514 Prevent Saving and Submitting if bed/bath > 10
  const someInputLimitError = Object.keys(hasInputLimitError).some(key => hasInputLimitError[key] === true);
  return (
    <>
      {transientValuation && selectedValuation &&
        <div className={classes.container}>
          <div className={classes.header}>
            <div className={classes.headerText}>
              <Tooltip title={selectedOpportunity.requestType ? `${selectedOpportunity.channel} - ${selectedOpportunity.stage ? selectedOpportunity.stage + ' - ' : ''}${selectedOpportunity.requestType}` : ''}>
                <Typography variant='subtitle2' className={classes.proformaHeader}>{selectedOpportunity.requestType ? `${selectedOpportunity.stage ? selectedOpportunity.stage + ' - ' : ''}${selectedOpportunity.requestType}` : selectedOpportunity.workstreamType}</Typography>
              </Tooltip>
              
              {/* {selectedOpportunity.underwriteStatus &&
                <UnderwriteStatusChip underwriteStatus={selectedOpportunity.underwriteStatus} style={{ marginLeft: 8 }} />
              } */}
            </div>
            <Divider />
          </div>
          <div className={classNames(classes.inputsContainer, viewingReadonlyMode && classes.grayBackground)}>
            {valuations.length > 0 && proformaValuation &&
              <>
                <div className={classes.inputs}>
                  <div className={classes.userSelectHeader}>
                    <div className={classes.userSelectLabel}>
                      <Badge badgeContent={valuations.length} color='primary' classes={{ badge: classes.userBadge }} overlap='rectangular'>
                        <Typography variant='body1' className={classes.headers}>Underwrites</Typography>
                      </Badge>
                    </div>
                    {/* Empty div to space icons to the end and keep header centered */}
                    <div></div>
                    <FormControl>
                      <Select
                        value={foundSelectedValuationIndex > -1 ? foundSelectedValuationIndex : ''}
                        onChange={onValuationIndexChange}
                        inputProps={{ name: 'selectedValuationIndex', id: 'valuationsDropdown' }}
                        classes={{ root: classes.userSelectRoot }}
                        disabled={viewingReadonlyMode}
                      >
                        {valuations.map((el, index) => (
                          <MenuItem key={el.id} value={index}>
                            <span
                              style={
                                el.id === 0
                                  ? {
                                      outline: '1px dotted gray',
                                      padding: '0 2px',
                                      display: 'inline-block',
                                    }
                                  : undefined
                              }
                            >
                              {el.user.fullName
                                ? el.user.fullName.toUpperCase()
                                : el.user.name.toUpperCase()}
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  {!isUsersValuation
                    ? <Paper className={classes.otherUserWarning}>
                      <Typography variant='subtitle2'>You are viewing another user's underwrite.</Typography>
                      {!viewingReadonlyMode ? <Typography variant='caption' className={classes.linkStyle} onClick={takeUWClick}>Take Underwrite</Typography> : null}
                    </Paper>
                    : null
                  }

                  {/* Valuation Inputs */}
                  <div className={classes.inputsHeader}>
                    <Typography variant='caption' color='primary' className={classes.headers}>Inputs</Typography>
                    {/* Empty div to space icons to the end and keep header centered */}
                    <div></div>
                  </div>
                  <div>
                    <PostAcqUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='rent'
                      inputSubheader='Rent'
                      inputValue={proformaValuation.valuationInput.rent}
                      inputValueType={proformaValuation.valuationInput.rentType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={() => {}}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError} 
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.rents : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      required={(requiredFields ?? []).includes('rent')}
                      isPostAcquisitionUnderwrite={true} />
                    <PostAcqUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='afterRepairValue'
                      inputSubheader='After Repair Value'
                      inputValue={proformaValuation.valuationInput.afterRepairValue}
                      inputValueType={proformaValuation.valuationInput.afterRepairValueType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={() => {}}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError} 
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.afterRepairValues : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      required={(requiredFields ?? []).includes('afterRepairValue')}
                      isPostAcquisitionUnderwrite={true} />
                    <PostAcqUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='lotValue'
                      inputSubheader='Lot Value'
                      inputValue={proformaValuation.valuationInput.lotValue}
                      inputValueType={proformaValuation.valuationInput.lotValueType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={() => {}}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError} 
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.lotValues : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      required={(requiredFields ?? []).includes('lotValue')}
                      isPostAcquisitionUnderwrite={true} />
                    <PostAcqUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='asIsValue'
                      inputSubheader='As Is Value'
                      inputValue={proformaValuation.valuationInput.asIsValue}
                      inputValueType={proformaValuation.valuationInput.asIsValueType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={() => {}}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError} 
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.asIsValues : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      required={(requiredFields ?? []).includes('asIsValue')}
                      isPostAcquisitionUnderwrite={true} />
                    <PostAcqUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='reducedRent'
                      inputSubheader='Reduced Rent'
                      inputValue={proformaValuation.valuationInput.reducedRent}
                      inputValueType={proformaValuation.valuationInput.reducedRentType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={() => {}}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError} 
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.reducedRents : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      required={(requiredFields ?? []).includes('reducedRent')}
                      isPostAcquisitionUnderwrite={true} />
                    <PostAcqUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='reducedAfterRepairValue'
                      inputSubheader='Reduced After Repair Value'
                      inputValue={proformaValuation.valuationInput.reducedAfterRepairValue}
                      inputValueType={proformaValuation.valuationInput.reducedAfterRepairValueType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={() => {}}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError} 
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.reducedAfterRepairValues : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      required={(requiredFields ?? []).includes('reducedAfterRepairValue')}
                      isPostAcquisitionUnderwrite={true} />
                  </div>
                  <Divider style={{ margin: '8px 0' }}/>
                  {/* Notes Input   */}
                  <div className={classes.noteContainer}>
                    <Box borderBottom={1} borderColor="grey" className={classes.noteSection} onClick={onClickNoteSection} disabled={!canEdit || viewingReadonlyMode}>
                      {proformaValuation.notes
                        ?
                        <Typography className={classes.noteContent} display='block' noWrap>
                          {proformaValuation.notes}
                        </Typography>
                        :
                        <Typography className={classes.notePlaceholder} display='block' noWrap>
                          Take a note...
                        </Typography>}
                    </Box>
                  </div>
                  {/* Buttons */}
                  {selectedOpportunity.consumedOn
                    ? <div style={{ textAlign: 'center', marginTop: 32 }}>
                        <Typography variant='subtitle2'>{`Task consumed on ${format(parseISO(selectedOpportunity.consumedOn), 'M/d/yyyy, h:mm a')}`}</Typography>
                      </div>
                    : !viewingReadonlyMode &&
                        <>
                          <div className={classes.buttonContainer}>
                            <Button
                          className={classes.halfWidthButton}
                          disabled={!canEdit || isSavingUW || !canSave || selectedOpportunity?.isProcessing || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole || someInputLimitError || userLacksRequiredRole}
                          variant='contained'
                          color='primary'
                          onClick={saveUWClick}
                          tabIndex={-1}>
                          {isSavingUW ?
                            <CircularProgress size={24} color='inherit' />
                            : 'Save'}
                        </Button>
                        {viewUnderwriteModalIsOpen
                          ? <Button
                              className={classes.halfWidthButton}
                              disabled={canSave ? (selectedOpportunity?.isProcessing || !canEdit || isSavingUW || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole || someInputLimitError || userLacksRequiredRole || selectedOpportunity.underwriteStatus === Constants.PostAcqUnderwriteStatus.SUBMITTED) : isSavingUW}
                              variant={canSave ? 'contained' : 'outlined'}
                              color='primary'
                              onClick={saveAndCloseModal}
                              tabIndex={-1}>
                              {isSavingUW ?
                                <CircularProgress size={24} color='inherit' /> :
                                canSave ?
                                  'Save & Exit'
                                  : 'Exit'}</Button>
                          : <Button
                            className={classes.halfWidthButton}
                            disabled={canSave ? (selectedOpportunity?.isProcessing || !canEdit || isSavingUW || !nextTask || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole || someInputLimitError || userLacksRequiredRole || selectedOpportunity.underwriteStatus === Constants.PostAcqUnderwriteStatus.SUBMITTED) : (!nextTask || isSavingUW)}
                            variant='contained'
                            color='primary'
                            onClick={saveAndNavigateToNextTask}
                            tabIndex={-1}>
                            {isSavingUW ?
                              <CircularProgress size={24} color='inherit' /> :
                              canSave ?
                                'Save & Next'
                                : 'Next'}</Button>
                        }
                      </div>
                      <div className={classes.buttonContainer}>
                        {selectedOpportunity.taskStatus === Constants.PostAcqTaskStatus.OPEN
                          ? <Button
                              className={classes.fullWidthButton}
                              disabled={selectedOpportunity.underwriteStatus !== Constants.PostAcqUnderwriteStatus.SAVED || canSave || isSavingUW || selectedOpportunity?.isProcessing || userHasUnderwriteReadOnlyRole || userLacksRequiredRole}
                              variant='contained'
                              color='primary'
                              onClick={handleTaskStatusChange}>
                              Close & Submit
                            </Button>
                          : <Button
                              className={classes.fullWidthButton}
                              disabled={isSavingUW || selectedOpportunity?.isProcessing || userHasUnderwriteReadOnlyRole || userLacksRequiredRole}
                              variant='contained'
                              color='primary'
                              onClick={handleTaskStatusChange}>
                              Reopen
                            </Button>
                        }
                      </div>
                    </>
                  }
                </div>
              </>
            }
          </div>
          <NoteModal
            isModalOpen={isNoteModalOpen}
            onCancel={onCancelNotes}
            onUpdate={onUpdateNotes}
            value={proformaValuation?.notes}
            isReadOnly={!canEdit}
            isPostAcquisitionUnderwrite={true}
          />
        </div>
      }
    </>
  )
}

const mapStateToProps = (state, ownProps) => {
  const uwState = ownProps.viewingReadonlyMode ? state.postAcqUWState.underwriteModalState : state.postAcqUWState;
  const selectedProformaInputs = selectedPostAcqProformaInputsSelector(state, ownProps.viewingReadonlyMode);
  const valuations = selectedProformaInputs?.valuations || [];
  const userHasUnderwriteReadOnlyRole = state.appState.user.roles.underwriteReadOnly

  return {
    userHasUnderwriteReadOnlyRole,
    selectedOpportunity: uwState.postAcqUWClientTask,
    selectedContainer: uwState.selectedContainer,
    selectedProformaInputs,
    valuations,
    selectedValuation: uwState.selectedValuation,
    isUsersValuation: isUsersPostAcqValuationSelector(state, ownProps.viewingReadonlyMode),
    transientValuation: uwState.transientValuation,
    valuationIsDirty: ownProps.viewingReadonlyMode ? false : state.postAcqUWState.valuationIsDirty,
    hasInputLimitError: state.postAcqUWState.hasInputLimitError,
    user: state.appState.user,
    isSavingUW: state.postAcqUWState.isSavingUW,
    nextTask: nextTaskSelector(state, Constants.Module.POSTACQ),
    taxonomy: postAcquisitionTaxonomySelector(state)
  };
}

const mapDispatchToProps = {
  closePostAcqTasks,
  openPostAcqTasks,
  setPostAcqHasInputLimitError,
  setPostAcqSelectedValuation,
  setPostAcqTransientValuationInputs,
  setPostAcqTransientValuationNotes,
  setPostAcqTransientValuation,
  savePostAcqModuleUW,
  appNotify,
};

const styles = theme => ({
  container: {
    gridArea: 'proforma',
    height: '100%',
    backgroundColor: '#fff',
    borderLeft: '2px solid rgba(0, 0, 0, 0.12)',
    paddingBottom: '10px',
    display: 'flex',
    flexDirection: 'column'
  },
  inputsContainer: {
    overflowY: 'auto',
  },
  headers: {
    fontWeight: 'bold',
    fontSize: '14px',
    alignSelf: 'center'
  },
  inputs: {
    marginLeft: '5px',
    marginRight: '5px',
  },
  userSelectHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginTop: theme.spacing(0.5)
  },
  userSelectLabel: {
    display: 'flex'
  },
  inputsHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    marginTop: theme.spacing(0.5)
  },
  proforma: {
    gridArea: 'proforma',
    paddingLeft: '5px',
    paddingRight: '5px',
    marginTop: theme.spacing(0.5)
  },
  header: {
    gridArea: 'expander',
    backgroundColor: '#fff',
    width: '100%',
    top: '0',
    zIndex: '1',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  noteContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '10px 0 10px 0',
  },
  noteSection: {
    width: '90%',
    cursor: 'pointer',
    padding: '5px'
  },
  noteContent: {
    width: '100%',
    height: '100%'
  },
  notePlaceholder: {
    color: 'gray'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  halfWidthButton: {
    flex: '0 0 calc(45% - 4px)',
    margin: '0 4px'
  },
  fullWidthButton: {
    flex: '0 0 90%',
  },
  proformaHeader: {
    color: 'black',
    fontSize: '1rem',
  },
  otherUserWarning: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    textAlign: 'center',
    padding: `${theme.spacing(1)}px 0`,
    backgroundColor: theme.palette.warning.main,
  },
  userSelectRoot: {
    marginLeft: theme.spacing(0.5)
  },
  userBadge: {
    top: 10,
    right: -16
  },
  linkStyle: {
    cursor: 'pointer',
    textDecoration: 'underline',
    color: '#0000EE',
  },
  grayBackground: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff'
  },
  inputTitle: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    paddingTop: '5px',
  },
  inputsSubheader: {
    marginRight: 8,
    fontWeight: 'bold',
    color: theme.palette.red.main,
    fontSize: '12px',
  },
  inputsSubheaderSelected: {
    marginRight: 8,
    fontWeight: 'bold',
    fontSize: '12px',
  },
});

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PostAcqUWProforma);